import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Container,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Paper,
    Snackbar,
    Stack,
    Typography
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'utils/axios';
import { useTranslation } from 'react-i18next';

export default function ForgotPasswordPage() {
    const { t, i18n } = useTranslation();
    const [snack, setSnack] = useState(null);

    const requestReset = async (email) => {
        const res = await axios.post(`password/forgot/`, {
            email,
            language: i18n.language
        });
        return res;
    };

    const validationSchema = Yup.object({
        email: Yup.string().email(t('auth.forgot_password.email_invalid')).required(t('auth.forgot_password.email_required'))
    });

    return (
        <>
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper elevation={1} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
                    <Stack spacing={2}>
                        <Typography variant="h4" fontWeight={700}>
                            {t('auth.forgot_password.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('auth.forgot_password.description')}
                        </Typography>

                        <Formik
                            initialValues={{ email: '' }}
                            validationSchema={validationSchema}
                            onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
                                const { status, data } = await requestReset(values.email);
                                setSubmitting(false);
                                if (status === 200) {
                                    setSnack({
                                        open: true,
                                        message: data?.message || t('auth.forgot_password.success_message'),
                                        severity: 'success'
                                    });
                                    resetForm();
                                } else {
                                    const apiError = data?.error || t('auth.forgot_password.error_message');
                                    setSnack({ open: true, message: apiError, severity: 'error' });
                                    // Field error fallbacks
                                    if (data?.email) setErrors({ email: data.email });
                                }
                            }}
                        >
                            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                                <form onSubmit={handleSubmit} noValidate>
                                    <Stack spacing={2.5}>
                                        <Box>
                                            <InputLabel htmlFor="email">{t('auth.forgot_password.email')}</InputLabel>
                                            <OutlinedInput
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                fullWidth
                                                placeholder={t('auth.forgot_password.email_placeholder')}
                                                error={Boolean(touched.email && errors.email)}
                                            />
                                            {touched.email && errors.email && <FormHelperText error>{errors.email}</FormHelperText>}
                                        </Box>

                                        <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                                            {isSubmitting ? t('auth.forgot_password.sending') : t('auth.forgot_password.send_button')}
                                        </Button>

                                        <Button href="/login" size="large" disabled={isSubmitting}>
                                            {t('auth.forgot_password.return_to_login')}
                                        </Button>
                                    </Stack>
                                </form>
                            )}
                        </Formik>
                    </Stack>
                </Paper>

                <Snackbar
                    open={!!snack?.open}
                    autoHideDuration={4000}
                    onClose={() => setSnack(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {snack ? (
                        <Alert onClose={() => setSnack(null)} severity={snack.severity} sx={{ width: '100%' }}>
                            {snack.message}
                        </Alert>
                    ) : null}
                </Snackbar>
            </Container>
        </>
    );
}
